/**
 * **The booth station's shared state** (F3): everything the station's parts read, held once.
 * `BoothProvider` holds it for `BoothStation`; each part — check-in, scan, the card, the walk-up, the count — reads
 * what it needs with `useBooth()`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { formatCountDay, formatTime } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import { findInDay, loadDay, newTagCode, type QueuedItem } from './boothOffline.js';
import {
  type Found,
  GROUP,
  HOUSE,
  SCAN,
  type Shift,
  type Tag,
  type Today,
  first,
  hours,
} from './BoothShared.js';
import { useBoothDesk } from './BoothDesk.js';
import { useBoothOffline } from './BoothOfflineQueue.js';

/** What queues when the network is down (7g), by the door it would have taken. */
const QUEUEABLE: Record<string, QueuedItem['kind'] | undefined> = {
  '/volunteers/booth/check-in': 'check-in',
  '/volunteers/booth/check-out': 'check-out',
  '/volunteers/booth/undo': 'undo',
  '/volunteers/booth/reassign': 'reassign',
};

function useBoothStation(label: string | undefined) {
  const [today, setToday] = useState<Today | null>(null);
  const [synced, setSynced] = useState<Date | null>(null);
  const [trouble, setTrouble] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<Found[] | null>(null);
  const [findSays, setFindSays] = useState('');
  const [picked, setPicked] = useState<Found | null>(null);
  const [placeOn, setPlaceOn] = useState('');
  const [walkUp, setWalkUp] = useState(false);
  /** A visitor (ADR 0032): the visitor card takes the walk-up form's place while open. */
  const [visitorOpen, setVisitorOpen] = useState(false);
  const [given, setGiven] = useState('');
  const [family, setFamily] = useState('');
  const [mobile, setMobile] = useState('');
  /** What was typed when nobody matched — the walk-up's eyebrow (state 10). */
  const [walkUpFrom, setWalkUpFrom] = useState('');
  /** Did you mean? — who already holds the walk-up's mobile (state 10). */
  const [samePhone, setSamePhone] = useState<{ personId: string; name: string } | null>(null);
  /**
   * The printer is out (state 25, the plain way): the operator says so from the Station menu. Tags
   * then print at another station or are skipped — this station never pretends to print.
   */
  const [printerOut, setPrinterOut] = useState(false);
  /** Checked out by the tag (state 23): the banner and the card as they leave. */
  const [left, setLeft] = useState<{
    name: string;
    area: string | null;
    inAt: string | null;
    outAt: string;
  } | null>(null);
  /** Print the tag again (state 19): the drawn card in place of the person's. */
  const [reprinted, setReprinted] = useState<{
    name: string;
    area: string;
    inAt: string | null;
  } | null>(null);
  /** Scanned twice (state 15): a tag read within half an hour of the check-in. */
  const [already, setAlready] = useState<{ at: string | null; personId: string } | null>(null);
  /** Covering (state 13): whose shift — the eyebrow reads “I'm here for Mike”. */
  const [covering, setCovering] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [tag, setTag] = useState<Tag | null>(null);
  /** A group's batch of tags (ON10 item 4), printed together. */
  const [batch, setBatch] = useState<Tag[] | null>(null);
  /** The group card, from the leader's QR. */
  const [group, setGroup] = useState<{
    code: string;
    at: Date;
    name: string;
    leader: string;
    says: string;
    members: (Found & { needsWaiver: boolean })[];
  } | null>(null);
  const [groupPick, setGroupPick] = useState<Set<string>>(new Set());
  /** The household card (state 6): the family page's QR, who is here, the family it counts for. */
  const [house, setHouse] = useState<{
    code: string;
    at: Date;
    name: string;
    familyId: string | null;
    says: string;
    members: (Found & { needsWaiver: boolean; minor?: boolean })[];
  } | null>(null);
  const [housePick, setHousePick] = useState<Set<string>>(new Set());
  /** Household checked in (state 7): what was recorded on each person. */
  const [houseDone, setHouseDone] = useState<{
    family: string | null;
    by: string | null;
    at: string;
    tags: { personId: string; name: string; area: string; startsAt: string; endsAt: string }[];
    recorded: { personId: string; name: string; waiverSignedAt: string | null; minor: boolean }[];
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState<
    | 'open'
    | 'close'
    | 'paper'
    | 'admin'
    | 'lists'
    | 'tomorrow'
    | 'whoshere'
    | 'shelter'
    | 'tag'
    | null
  >(null);
  // The guest screen (the two screens): whether it is on, and what it says back.
  const [guest, setGuest] = useState<{
    on: boolean;
    state: string;
    confirmed: boolean;
    signed: boolean;
    signedOn?: string | null;
    teen?: boolean;
    /** The household on its own guest screen (G06): who tapped "here". */
    household?: { here: string[] };
  } | null>(null);
  /** WAIVER on the card, always (Oct 6): signed (and when) or not yet. */
  const [waiverOf, setWaiverOf] = useState<{ needs: boolean; signedOn: string | null } | null>(
    null,
  );
  const [guestHint, setGuestHint] = useState<string | null>(null);
  /** Who the guest screen is showing — its "signed" counts for that person's card only. */
  const [guestShowing, setGuestShowing] = useState<string | null>(null);
  /** Whose waiver is up on the guest screen — a household's or a group's card learns it is signed. */
  const [guestFor, setGuestFor] = useState<string | null>(null);
  const [needsWaiver, setNeedsWaiver] = useState(false);
  /** A walk-up made and waiting on the year's waiver before the check-in goes through. */
  const [pending, setPending] = useState<{
    personId: string;
    name: string;
    shiftId: string;
  } | null>(null);
  const search = useRef<HTMLInputElement>(null);
  const {
    operator,
    setOperator,
    desk,
    deskOpen,
    setDeskOpen,
    onNow,
    standby,
    opName,
    loadDesk,
    stillAsk,
    setStillAsk,
    lastTouch,
  } = useBoothDesk();
  // The network back and the queue sealed: say how it went, and read the day again.
  const { queue, offlineSince, offline, goneOffline, keepDay, enqueue, setDayCache } =
    useBoothOffline((says) => {
      setDone(says);
      load();
    });
  const [sendTo, setSendTo] = useState<Record<string, string>>({});
  const [why, setWhy] = useState('');
  const [issueItem, setIssueItem] = useState<'radio' | 'key'>('radio');
  const [issueNumber, setIssueNumber] = useState('');
  // The family question (ON11 item 2): the day's short list, and what the operator picked.
  /** The family choices read again after the guest screen answered (G05). */
  const [choicesTick, setChoicesTick] = useState(0);
  const [choices, setChoices] = useState<
    { familyId: string; responsibleParty: string; address: string | null; why?: string }[] | null
  >(null);
  /** Why are you here, at the desk: the answer being picked, and a service group. */
  const [serviceGroup, setServiceGroup] = useState('');
  const [creditPick, setCreditPick] = useState('');
  const [holding, setHolding] = useState(false);
  const [hand, setHand] = useState<
    | { kind: 'undo'; shiftId: string }
    | { kind: 'reassign'; shiftId: string; to: string }
    | { kind: 'swap'; shiftId: string; signupId: string }
    | { kind: 'cover' }
    | null
  >(null);

  const load = useCallback((): void => {
    loadDesk();
    api<Today>('GET', '/volunteers/booth/today')
      .then((t) => {
        setToday(t);
        setSynced(new Date());
        setTrouble(null);
        try {
          window.localStorage.setItem('cider-booth-today', JSON.stringify(t));
        } catch {
          // Storage refused: nothing to keep.
        }
      })
      .catch((e: unknown) => {
        if (goneOffline(e)) {
          try {
            const kept = window.localStorage.getItem('cider-booth-today');
            if (kept) setToday((t) => t ?? (JSON.parse(kept) as Today));
          } catch {
            // Storage refused: the bar says offline and the day is empty.
          }
          return;
        }
        setTrouble(sentence(e));
      });
  }, [loadDesk, goneOffline]);
  useEffect(load, [load]);
  // What the guest screen says back — on, confirmed, signed. One small read every three seconds.
  const readGuest = useCallback((): void => {
    api<{
      on: boolean;
      state: string;
      confirmed: boolean;
      signed: boolean;
      signedOn?: string | null;
      teen?: boolean;
      household?: { here: string[] };
    }>('GET', '/volunteers/booth/guest?from=operator')
      .then(setGuest)
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    readGuest();
    const t = setInterval(readGuest, 3_000);
    return () => {
      clearInterval(t);
    };
  }, [readGuest]);

  // The family choices: read once per person, and again when they say "school family".
  const pickedId = picked?.personId ?? null;
  const askChoices =
    !!picked && (!picked.creditAnswered || picked.whyHere?.reason === 'school_family');
  useEffect(() => {
    setCreditPick('');
    setChoices(null);
    if (!pickedId || !askChoices) return;
    api<{
      rows: { familyId: string; responsibleParty: string; address: string | null; why?: string }[];
    }>('GET', `/volunteers/booth/credit-choices?personId=${pickedId}`)
      .then((r) => {
        setChoices(r.rows);
      })
      .catch(() => {
        setChoices(null);
      });
  }, [pickedId, askChoices, choicesTick]);

  // NO on the guest screen (Oct 6): straight into "Someone else is working it", search ready.
  const saidNo = useRef(false);
  useEffect(() => {
    if (guest?.state !== 'notme') {
      saidNo.current = false;
      return;
    }
    if (saidNo.current || !picked) return;
    saidNo.current = true;
    const row = picked.today.find((t) => t.state === 'expected' && t.signupId);
    if (row?.signupId) setHand({ kind: 'swap', shiftId: row.shiftId, signupId: row.signupId });
  }, [guest?.state, picked]);

  /** Put something on the guest screen for one person (CONFIRM, WAIVER, TAG, THANK YOU) or clear it. */
  const show = (
    state:
      | 'welcome'
      | 'confirm'
      | 'hold'
      | 'walkup'
      | 'why'
      | 'household'
      | 'wrongday'
      | 'waiver'
      | 'tag'
      | 'thanks',
    personId?: string,
    shiftId?: string | null,
    groupCode?: string,
    householdCode?: string,
  ): void => {
    // "Your tag is printing" only where one prints: not on the tablet, not for an area with no tags.
    if (state === 'tag' && (tablet || (shiftId && tagsByShift.get(shiftId) === false)))
      state = 'thanks';
    api<{ needsWaiver: boolean; signedOn?: string | null; state?: string; signed?: boolean }>(
      'POST',
      '/volunteers/booth/guest/show',
      {
        state,
        personId: personId ?? null,
        shiftId: shiftId ?? null,
        ...(groupCode ? { groupCode } : {}),
        ...(householdCode ? { householdCode } : {}),
      },
    )
      .then((r) => {
        // The screen's own answer, at once: a "signed" left from the last person never counts for
        // the next one (a household's or a group's row would turn ready before they sign).
        setGuest((g) => (g ? { ...g, state: r.state ?? state, signed: r.signed ?? g.signed } : g));
        setGuestFor(state === 'waiver' ? (personId ?? null) : null);
        setGuestShowing(personId ?? null);
        if (state === 'confirm' || state === 'waiver') {
          setNeedsWaiver(r.needsWaiver);
          setWaiverOf({ needs: r.needsWaiver, signedOn: r.signedOn ?? null });
        }
        readGuest();
      })
      .catch(() => undefined);
  };

  /**
   * **Open the guest screen.** On the second display, full screen, where the browser can place it
   * (the window-management permission, asked once and remembered by the browser); otherwise a
   * plain window and one line on how to move it.
   */
  const openGuest = async (): Promise<void> => {
    if (guest?.on) return;
    const wm = window as unknown as {
      getScreenDetails?: () => Promise<{
        screens: { availLeft: number; availTop: number; availWidth: number; availHeight: number }[];
        currentScreen: unknown;
      }>;
    };
    let placed = false;
    if (wm.getScreenDetails) {
      try {
        const d = await wm.getScreenDetails();
        const other = d.screens.find((x) => x !== d.currentScreen);
        if (other) {
          window.open(
            '/booth/guest',
            'cider-guest',
            `popup,left=${String(other.availLeft)},top=${String(other.availTop)},width=${String(other.availWidth)},height=${String(other.availHeight)}`,
          );
          placed = true;
        }
      } catch {
        placed = false;
      }
    }
    if (!placed) {
      window.open('/booth/guest', 'cider-guest', 'popup,width=1280,height=800');
      setGuestHint('Drag it to the other screen and tap it once — it remembers.');
    } else setGuestHint('Tap the guest screen once — it goes full screen and remembers.');
    setTimeout(readGuest, 2_000);
  };

  // Results as you type, two letters on; the newest answer wins.
  const asked = useRef(0);
  useEffect(() => {
    const text = q.trim();
    if (text.length < 2 || SCAN.test(text) || GROUP.test(text) || HOUSE.test(text)) {
      setFound(null);
      return;
    }
    const n = ++asked.current;
    const t = setTimeout(() => {
      api<{ rows: Found[]; says: string }>(
        'GET',
        `/volunteers/booth/find?q=${encodeURIComponent(text)}`,
      )
        .then((r) => {
          if (n !== asked.current) return;
          setFound(r.rows);
          setFindSays(r.says);
        })
        .catch((e: unknown) => {
          // No network (7g): the day the station keeps answers instead.
          if (goneOffline(e)) {
            if (n !== asked.current) return;
            const rows = findInDay(loadDay(), text);
            setFound(rows);
            setFindSays(
              rows.length
                ? `${String(rows.length)} found — today’s roster, kept here`
                : 'Not on today’s roster kept here. A new person needs the network.',
            );
            return;
          }
          setFindSays(sentence(e));
        });
    }, 180);
    return () => {
      clearTimeout(t);
    };
  }, [q, goneOffline]);

  /** Why are you here, answered at the desk (or read back from the guest screen). */
  const answerWhy = (reason: string): void => {
    if (!picked) return;
    const who = picked;
    act(
      '/volunteers/booth/why',
      {
        personId: who.personId,
        reason,
        serviceGroup: reason === 'service_hours' ? serviceGroup : null,
      },
      (r) => {
        setDone(`${who.name}: ${r.says}`);
        setPicked({
          ...who,
          whyHere: {
            reason,
            serviceGroup: reason === 'service_hours' ? serviceGroup : null,
            on: new Date().toISOString(),
          },
        });
      },
      true,
    );
  };

  /** Link to the family and check in — one press (Rebuild 29), the link recorded first. */
  const linkAndCheckIn = (p: Found, s: Found['today'][number], familyId: string): void => {
    setBusy(true);
    api<{ says: string }>('POST', '/volunteers/booth/link-family', {
      personId: p.personId,
      familyId,
    })
      .then((r) => {
        setDone(`${p.name}: ${r.says}`);
        setChoices(
          (choices ?? []).map((c) =>
            c.familyId === familyId ? { ...c, why: 'the family we hold you in' } : c,
          ),
        );
        checkIn(p, s, false);
      })
      .catch((e: unknown) => {
        setDone(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  /** The household's pick, the waivers it still waits on, and its batch check-in. */
  const houseChosen = house ? house.members.filter((m) => housePick.has(m.personId)) : [];
  const houseUnsigned = houseChosen.filter((m) => m.needsWaiver).length;
  const groupWaiversLeft = group
    ? group.members.filter((m) => m.needsWaiver && m.today.some((t) => t.state === 'expected'))
        .length
    : 0;
  // Tapped on the household's guest screen (G06): the card's who-is-here follows — working
  // members only, since only they check in.
  const hereKey = guest?.state === 'household' ? (guest.household?.here ?? []).join(',') : null;
  useEffect(() => {
    // Until somebody taps, the card keeps its own pick (everyone working today).
    if (!hereKey) return;
    const tapped = new Set(hereKey ? hereKey.split(',') : []);
    setHousePick((pick) => {
      if (!house) return pick;
      const working = house.members
        .filter((m) => m.today.some((t) => t.state === 'expected'))
        .map((m) => m.personId);
      return new Set(working.filter((id) => tapped.has(id)));
    });
  }, [hereKey, house]);
  // Answered on the guest screen (G04): the card reads the person again, so WHY HERE says what
  // they chose.
  const wasWhy = useRef(false);
  useEffect(() => {
    const now = guest?.state === 'why' || guest?.state === 'whyfamily';
    if (wasWhy.current && !now && picked) {
      const who = picked;
      api<{ rows: Found[] }>('GET', `/volunteers/booth/find?q=${encodeURIComponent(who.name)}`)
        .then((f) => {
          const row = f.rows.find((x) => x.personId === who.personId);
          if (row) setPicked((p) => (p?.personId === row.personId ? row : p));
          setChoicesTick((t) => t + 1);
        })
        .catch(() => undefined);
    }
    wasWhy.current = now;
  }, [guest?.state, picked]);
  // Signed on the guest screen: the household's or the group's row says so, and the big button
  // counts it (states 7 and 9).
  useEffect(() => {
    if (!guest?.signed || !guestFor) return;
    const sealed = <T extends { personId: string; needsWaiver: boolean }>(rows: T[]): T[] =>
      rows.map((m) => (m.personId === guestFor ? { ...m, needsWaiver: false } : m));
    setHouse((h) => (h ? { ...h, members: sealed(h.members) } : h));
    setGroup((g) => (g ? { ...g, members: sealed(g.members) } : g));
    setGroupPick((p) => new Set([...p, guestFor]));
    setGuestFor(null);
  }, [guest?.signed, guestFor]);
  const andNames = (rows: { name: string }[]): string => {
    const n = rows.map((r) => first(r.name));
    return n.length > 1
      ? `${n.slice(0, -1).join(', ')} and ${n[n.length - 1] ?? ''}`
      : (n[0] ?? '');
  };
  const checkInHouse = (ids: string[]): void => {
    if (!house) return;
    const h = house;
    setBusy(true);
    api<{
      tags: {
        personId: string;
        name: string;
        area: string;
        startsAt: string;
        endsAt: string;
        tag: string;
      }[];
      skipped: { name: string; why: string }[];
      says: string;
      recorded?: {
        personId: string;
        name: string;
        waiverSignedAt: string | null;
        minor: boolean;
      }[];
      family?: string | null;
      by?: string | null;
    }>('POST', '/volunteers/booth/household/check-in', { code: h.code, personIds: ids })
      .then((r) => {
        if (r.tags.length)
          setHouseDone({
            family: r.family ?? null,
            by: r.by ?? null,
            at: new Date().toISOString(),
            tags: r.tags,
            recorded: r.recorded ?? [],
          });
        setTag(null);
        setBatch(
          r.tags.map((t) => ({
            name: t.name,
            area: t.area,
            hours: hours(t.startsAt, t.endsAt),
            day,
            code: t.tag,
          })),
        );
        setDone(
          `${r.says}${r.skipped.length ? ` ${r.skipped.map((x) => `${x.name}: ${x.why}`).join(' · ')}` : ''}`,
        );
        setHouse(null);
        load();
        if (r.tags.length)
          setTimeout(() => {
            printTag(r.tags.map((t) => t.area));
          }, 50);
      })
      .catch((e: unknown) => {
        setDone(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  /** A result picked (a click, or 1 – 5): the card, the guest screen asks, the box keeps focus. */
  const pickResult = (p: Found): void => {
    setReprinted(null);
    setLeft(null);
    setPicked(p);
    setNeedsWaiver(p.needsWaiver ?? false);
    setPlaceOn(shortest[0]?.shiftId ?? '');
    setQ('');
    setFound(null);
    // The operator's own name never reaches the guest screen (Oct 6).
    // Not on today (G15): the guest screen says the day they are on, and asks nothing.
    if (p.personId !== desk?.personId)
      if (p.today.length === 0 && p.otherDays.length) show('wrongday', p.personId);
      else show('confirm', p.personId, p.today[0]?.shiftId ?? null);
    search.current?.focus();
  };

  /** Print the tag again: the old code stops; offline, a code made here and the reprint queued. */
  const reprint = (who: Found, s: Found['today'][number]): void => {
    setBusy(true);
    api<{ name: string; area: string; startsAt: string; endsAt: string; tag: string }>(
      'POST',
      '/volunteers/booth/reprint',
      { shiftId: s.shiftId, personId: who.personId },
    )
      .then((r) => {
        setTag({
          name: r.name,
          area: r.area,
          hours: hours(r.startsAt, r.endsAt),
          day,
          code: r.tag,
        });
        setDone(`${who.name}: the tag prints again.`);
        setReprinted({ name: who.name, area: s.area, inAt: s.inAt ?? null });
        reset();
        setTimeout(() => {
          printTag();
        }, 50);
      })
      .catch((e: unknown) => {
        if (goneOffline(e)) {
          // No network (7g): a new code made here; the reprint queues.
          const tag = newTagCode();
          enqueue({ kind: 'reprint', personId: who.personId, shiftId: s.shiftId, tag });
          setTag({
            name: who.name,
            area: s.area,
            hours: hours(s.startsAt, s.endsAt),
            day,
            code: tag,
          });
          setDone(`${who.name}: the tag prints again — queued with its time.`);
          setTimeout(() => {
            printTag();
          }, 50);
          return;
        }
        setDone(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  /** New person (the walk-up): what was typed becomes the name; the shortest shift first. */
  const startWalkUp = (): void => {
    const words = q.trim().split(/\s+/);
    setGiven(words[0] ? words[0].charAt(0).toUpperCase() + words[0].slice(1) : '');
    setFamily(
      words[1]
        ? words
            .slice(1)
            .join(' ')
            .replace(/^./, (c) => c.toUpperCase())
        : '',
    );
    setPlaceOn(shortest[0]?.shiftId ?? placeChoices[0]?.shiftId ?? '');
    setPicked(null);
    setWalkUpFrom(q.trim());
    setSamePhone(null);
    setWalkUp(true);
  };
  // Did you mean? (state 10): once the mobile is a whole number, who already holds it.
  useEffect(() => {
    if (!walkUp || mobile.replace(/\D/g, '').length < 10) {
      setSamePhone(null);
      return;
    }
    let live = true;
    api<{ match: { personId: string; name: string } | null }>(
      'GET',
      `/volunteers/booth/same-phone?mobile=${encodeURIComponent(mobile)}`,
    )
      .then((r) => {
        if (live) setSamePhone(r.match);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [walkUp, mobile]);

  /** Undo from the rail's last five: that person's card, with the undo open on that shift. */
  const undoFromRail = (personId: string, name: string, shiftId: string): void => {
    api<{ rows: Found[] }>('GET', `/volunteers/booth/find?q=${encodeURIComponent(name)}`)
      .then((r) => {
        const p = r.rows.find((x) => x.personId === personId);
        if (!p) return;
        setQ('');
        setFound(null);
        setWalkUp(false);
        setVisitorOpen(false);
        setPicked(p);
        setHand({ kind: 'undo', shiftId });
      })
      .catch((e: unknown) => {
        setDone(sentence(e));
      });
  };

  const reset = (): void => {
    setAlready(null);
    setCovering(null);
    setHouseDone(null);
    setNeedsWaiver(false);
    setBatch(null);
    setQ('');
    setFound(null);
    setPicked(null);
    setWalkUp(false);
    setVisitorOpen(false);
    setPlaceOn('');
    setGiven('');
    setFamily('');
    setMobile('');
    setHand(null);
    setWhy('');
    search.current?.focus();
  };

  const act = (
    path: string,
    body: Record<string, unknown>,
    after: (r: { says: string; name?: string; tag?: string }) => void,
    /** An answer on the card (why here, credit it): the card stays; the day reloads. */
    keepCard = false,
  ): void => {
    setBusy(true);
    setDone(null);
    api<{ says: string; name?: string; tag?: string }>('POST', path, body)
      .then((r) => {
        after(r);
        if (!keepCard) reset();
        load();
      })
      .catch((e: unknown) => {
        if (goneOffline(e)) {
          const kind = QUEUEABLE[path];
          if (!kind) {
            setDone('That needs the network. It is back in a moment — or take it on paper.');
            return;
          }
          const tag = kind === 'check-in' || kind === 'reassign' ? newTagCode() : null;
          const b = body as Record<string, string | boolean | null | undefined> & {
            proceed?: { name?: string; reason: string } | null;
          };
          enqueue({
            kind,
            personId: (b['personId'] as string | undefined) ?? null,
            shiftId:
              ((kind === 'reassign' ? b['fromShiftId'] : b['shiftId']) as string | undefined) ??
              null,
            signupId: (b['signupId'] as string | null | undefined) ?? null,
            walkUp: b['walkUp'] === true,
            proceed: b.proceed ?? null,
            toShiftId: (b['toShiftId'] as string | undefined) ?? null,
            name: (b['name'] as string | undefined) ?? null,
            reason: (b['reason'] as string | null | undefined) ?? null,
            tag,
          });
          after({
            says: 'Queued with its time — it seals when the network is back.',
            ...(tag ? { tag } : {}),
          });
          reset();
          return;
        }
        setDone(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const day = today ? formatCountDay(today.day) : '';
  const shortest = today?.shifts.filter((s) => s.short > 0) ?? [];
  const placeChoices = today?.shifts ?? [];

  const checkIn = (
    p: Found,
    s: Found['today'][number] | Shift,
    walkUpToo: boolean,
    proceed?: { name: string; reason: string },
    /** Checked in without a family — a school family not linked yet (the booth chair links them). */
    noFamily = false,
  ): void => {
    // No network (7g): the waiver is signed on the guest screen, which needs the server.
    if (offline && loadDay()?.people.find((x) => x.personId === p.personId)?.needsWaiver) {
      setDone(
        `${p.name} has not signed this year’s waiver. Signing needs the network — it is back in a moment.`,
      );
      return;
    }
    act(
      '/volunteers/booth/check-in',
      {
        shiftId: s.shiftId,
        personId: p.personId,
        signupId: 'signupId' in s ? s.signupId : null,
        walkUp: walkUpToo,
        ...(proceed ? { proceed } : {}),
        ...(noFamily || creditPick === 'no'
          ? { forCredit: false }
          : creditPick
            ? { forCredit: true, creditFamilyId: creditPick }
            : p.whyHere
              ? p.whyHere.reason !== 'school_family'
                ? { forCredit: false }
                : linkedFamily
                  ? { forCredit: true, creditFamilyId: linkedFamily.familyId }
                  : {}
              : {}),
      },
      (r) => {
        setDone(`${p.name}: ${r.says}`);
        setTag({
          name: p.name,
          area: s.area,
          hours: hours(s.startsAt, s.endsAt),
          day,
          code: (r as { tag?: string }).tag,
          issued: p.issue.out.map((o) => `${o.itemName} ${o.number}`.toUpperCase()).join(' · '),
        });
        show('tag', p.personId, s.shiftId);
      },
    );
  };

  /** The signup stays, the person changes (7b): then the worker's own card, with that shift. */
  const workedBy = (
    signupId: string,
    worker: Found,
    shift: Found['today'][number],
    kind: 'swap' | 'cover',
    /** Covering (state 13): one press — the shift moves, and the cover is checked in. */
    andCheckIn = false,
  ): void => {
    setBusy(true);
    api<{ says: string }>('POST', '/volunteers/booth/worked-by', {
      signupId,
      personId: worker.personId,
      kind,
      name: opName,
    })
      .then((r) => {
        setDone(r.says);
        setHand(null);
        const card: Found = {
          ...worker,
          today: [{ ...shift, signupId, state: 'expected', workedBy: null }],
        };
        setPicked(card);
        show('confirm', worker.personId, shift.shiftId);
        const moved = card.today[0];
        if (andCheckIn && moved && !needsWaiver) checkIn(card, moved, false);
      })
      .catch((e: unknown) => {
        setDone(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  /** The card's shift for the big button: the one expected, else the one they are in. */
  const expectedShift = picked?.today.find((x) => x.state === 'expected' && !x.workedBy) ?? null;
  /** The area of the shift on the card uses no tags (the area sets it; state 11). */
  const tagless = expectedShift?.tagsRequired === false;
  const inShift = picked?.today.find((x) => x.state === 'in') ?? null;
  /** Undo (state 24): when the check-in being undone was made, for the eyebrow. */
  const undoInAt = ((): string => {
    if (hand?.kind !== 'undo' || !picked) return '';
    const t = picked.today.find((x) => x.shiftId === hand.shiftId);
    return t?.inAt ? ` · IN ${formatTime(t.inAt)}` : '';
  })();
  /** A chair (ruling 2): helping today in their area, and the kit — never a check-in. */
  const isChair = !!picked?.issue.eligible;
  // The walk-around unit (state 11): the tag is at the booth, and radios and keys live at the desk.
  const tablet = !!today?.walkAround;
  // The area sets whether its volunteers wear a tag (state 11); an area the day does not list does.
  const tagsByShift = new Map(
    (today?.shifts ?? []).map((x) => [x.shiftId, x.tagsRequired !== false]),
  );
  const tagsByArea = new Map((today?.shifts ?? []).map((x) => [x.area, x.tagsRequired !== false]));
  const usesTags = (area: string): boolean => tagsByArea.get(area) ?? true;
  /** Prints the tags on the page — none on the tablet, none where every area named uses no tags. */
  const printTag = (areas?: string[]): void => {
    if (tablet || (areas && !areas.some(usesTags))) return;
    window.print();
  };
  /** Under the area's age (state 17): a hard stop, no proceed anyway — where instead. */
  const tooYoung =
    expectedShift?.minimumAge &&
    typeof picked?.age === 'number' &&
    picked.age < expectedShift.minimumAge
      ? { min: expectedShift.minimumAge, age: picked.age, area: expectedShift.area }
      : null;
  const instead = tooYoung
    ? placeChoices.filter(
        (x) =>
          x.short > 0 &&
          x.shiftId !== expectedShift?.shiftId &&
          (x.minimumAge ?? 0) <= tooYoung.age &&
          x.startsAt < (expectedShift?.endsAt ?? '') &&
          x.endsAt > (expectedShift?.startsAt ?? ''),
      )
    : [];
  /** The teen rule (state 8): under 18 with this year's waiver unsigned — a parent signs. */
  /**
   * Signed on the guest screen — for this card's person. The screen's "signed" belongs to whoever it
   * is showing; the desk's own person is never put on it unasked, so a signature left from the last
   * person never counts here (the walk found the card saying "signed just now" for the wrong one).
   */
  const signedHere = !!guest?.signed && !!picked && guestShowing === picked.personId;
  const teenWaits = !!picked?.minor && needsWaiver && !signedHere;

  /** Why here, in words (Rebuild 29). */
  const WHY_WORDS: Record<string, string> = {
    parishioner: 'parishioner',
    community: 'community member',
    school_family: 'school family — for our commitment',
    service_hours: 'service hours',
  };
  const linkedFamily = choices?.find((c) => c.why === 'the family we hold you in') ?? null;
  const schoolFamily = picked?.whyHere?.reason === 'school_family';
  /** A school family not yet linked: the family picked to link, and the big button does both. */
  const toLink =
    schoolFamily && !linkedFamily && creditPick && creditPick !== 'no'
      ? (choices?.find((c) => c.familyId === creditPick) ?? null)
      : null;
  /** MISSING, in words: what stands between this person and Check in. */
  const missing: string[] = picked
    ? [
        ...(needsWaiver && !signedHere ? ['waiver'] : []),
        ...(picked.emergency === false ? ['emergency contact'] : []),
        ...(picked.ageKnown === false ? ['under 18?'] : []),
      ]
    : [];

  /** Who's at the desk (ruling 1): nothing else on the station until someone has picked their name. */
  const signingIn = deskOpen || !desk;
  const stationName = (today?.label ?? label ?? 'the station').replace(/^the booth · /i, '');
  // The search box owns focus (Rebuild 29): a scanner's keystrokes never land anywhere else.
  useEffect(() => {
    if (signingIn) return;
    const keep = (e: KeyboardEvent): void => {
      const t = e.target;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) return;
      if (t instanceof HTMLSelectElement) return;
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) search.current?.focus();
    };
    // A press of a button gives the focus back to the box; a field the operator clicks keeps it.
    const back = (e: MouseEvent): void => {
      const t = e.target;
      if (!(t instanceof Element) || !t.closest('button, summary')) return;
      setTimeout(() => {
        const a = document.activeElement;
        if (
          a instanceof HTMLInputElement ||
          a instanceof HTMLTextAreaElement ||
          a instanceof HTMLSelectElement
        )
          return;
        search.current?.focus();
      }, 0);
    };
    window.addEventListener('keydown', keep, true);
    window.addEventListener('click', back, true);
    return () => {
      window.removeEventListener('keydown', keep, true);
      window.removeEventListener('click', back, true);
    };
  }, [signingIn]);

  const title = signingIn
    ? deskOpen && desk
      ? 'I’m taking over'
      : 'Who’s at the desk?'
    : panel === 'open'
      ? 'Open the booths'
      : panel === 'close'
        ? 'Close the booths'
        : panel === 'paper'
          ? 'Enter the paper'
          : panel === 'admin'
            ? 'Admin station'
            : panel === 'whoshere'
              ? 'Who’s here now'
              : panel === 'shelter'
                ? 'Shelter roll'
                : panel === 'lists'
                  ? 'Lists'
                  : panel === 'tomorrow'
                    ? 'Tomorrow'
                    : panel === 'tag'
                      ? 'The tag'
                      : walkUp
                        ? 'NEW PERSON'
                        : left && !picked
                          ? left.name.toUpperCase()
                          : reprinted && !picked
                            ? 'Print the tag again'
                            : picked && hand?.kind === 'undo'
                              ? 'Undo a check-in'
                              : picked && hand?.kind === 'reassign'
                                ? `REASSIGN ${picked.name.toUpperCase()}`
                                : picked && hand?.kind === 'swap'
                                  ? 'WHO IS ACTUALLY WORKING?'
                                  : picked && hand?.kind === 'cover'
                                    ? picked.name.toUpperCase()
                                    : picked
                                      ? picked.name.toUpperCase()
                                      : house
                                        ? house.name.toUpperCase()
                                        : group
                                          ? group.name.toUpperCase()
                                          : 'READY';

  return {
    today,
    setToday,
    synced,
    setSynced,
    trouble,
    setTrouble,
    q,
    setQ,
    found,
    setFound,
    findSays,
    setFindSays,
    picked,
    setPicked,
    placeOn,
    setPlaceOn,
    walkUp,
    setWalkUp,
    visitorOpen,
    setVisitorOpen,
    given,
    setGiven,
    family,
    setFamily,
    mobile,
    setMobile,
    walkUpFrom,
    setWalkUpFrom,
    samePhone,
    setSamePhone,
    printerOut,
    setPrinterOut,
    left,
    setLeft,
    reprinted,
    setReprinted,
    already,
    setAlready,
    covering,
    setCovering,
    done,
    setDone,
    tag,
    setTag,
    batch,
    setBatch,
    group,
    setGroup,
    groupPick,
    setGroupPick,
    house,
    setHouse,
    housePick,
    setHousePick,
    houseDone,
    setHouseDone,
    busy,
    setBusy,
    panel,
    setPanel,
    guest,
    setGuest,
    waiverOf,
    setWaiverOf,
    guestHint,
    setGuestHint,
    guestShowing,
    setGuestShowing,
    guestFor,
    setGuestFor,
    needsWaiver,
    setNeedsWaiver,
    pending,
    setPending,
    search,
    setDayCache,
    queue,
    offlineSince,
    offline,
    goneOffline,
    operator,
    setOperator,
    desk,
    deskOpen,
    setDeskOpen,
    onNow,
    standby,
    sendTo,
    setSendTo,
    opName,
    why,
    setWhy,
    issueItem,
    setIssueItem,
    issueNumber,
    setIssueNumber,
    choicesTick,
    setChoicesTick,
    choices,
    setChoices,
    serviceGroup,
    setServiceGroup,
    creditPick,
    setCreditPick,
    holding,
    setHolding,
    hand,
    setHand,
    loadDesk,
    keepDay,
    load,
    lastTouch,
    stillAsk,
    setStillAsk,
    enqueue,
    readGuest,
    pickedId,
    askChoices,
    saidNo,
    show,
    openGuest,
    asked,
    answerWhy,
    linkAndCheckIn,
    houseChosen,
    houseUnsigned,
    groupWaiversLeft,
    hereKey,
    wasWhy,
    andNames,
    checkInHouse,
    pickResult,
    reprint,
    startWalkUp,
    undoFromRail,
    reset,
    act,
    day,
    shortest,
    placeChoices,
    checkIn,
    workedBy,
    expectedShift,
    tagless,
    inShift,
    undoInAt,
    isChair,
    tablet,
    tagsByShift,
    tagsByArea,
    usesTags,
    printTag,
    tooYoung,
    instead,
    signedHere,
    teenWaits,
    WHY_WORDS,
    linkedFamily,
    schoolFamily,
    toLink,
    missing,
    signingIn,
    stationName,
    title,
  };
}

export type BoothState = ReturnType<typeof useBoothStation>;
const BoothContext = createContext<BoothState | null>(null);

export function BoothProvider({
  label,
  children,
}: {
  label: string | undefined;
  children: ReactNode;
}): ReactNode {
  return <BoothContext.Provider value={useBoothStation(label)}>{children}</BoothContext.Provider>;
}
/** The station's state, from inside the station. */
export function useBooth(): BoothState {
  const s = useContext(BoothContext);
  if (!s) throw new Error('useBooth outside the booth station');
  return s;
}
